import { Component, computed, inject, input } from '@angular/core';
import { of } from 'rxjs';
import { SkeletonModule } from '@openng/optimus-ui/skeleton';
import { CurrencyFormatPipe, rxResourceFixed } from '@zayan-erp/shared';
import { DsmLotIncentiveService } from '../../../services/dsm-lot-incentive.service';
import { LotAddendumIncentiveApplicationMethod } from '../../../enums/dsm-asp-incentives.enums';
import {
  DsmAddendumIncentivePreview,
  DsmAddendumIncentivePreviewProduct
} from '../../../models/dsm-lot-incentives.models';

interface AllocationRow {
  id: string;
  label: string;
  cost: number;
  allocated: number;
  percentage: number;
  skipped: boolean;
}

@Component({
  selector: 'dsm-addendum-incentive-preview',
  standalone: true,
  imports: [SkeletonModule, CurrencyFormatPipe],
  templateUrl: './dsm-addendum-incentive-preview.component.html'
})
export class DsmAddendumIncentivePreviewComponent {
  private readonly lotIncentiveService = inject(DsmLotIncentiveService);

  readonly addendumId = input.required<string>();
  /** Debounced amount the user wants to apply. Null/0 disables the preview. */
  readonly amount = input<number | null>(null);
  readonly method = input<LotAddendumIncentiveApplicationMethod>(
    LotAddendumIncentiveApplicationMethod['Highest cost first']
  );

  readonly hasValidAmount = computed(() => {
    const amt = this.amount();
    return amt !== null && amt !== undefined && amt > 0;
  });

  readonly previewResource = rxResourceFixed<
    DsmAddendumIncentivePreview | null,
    { addendumId: string; amount: number | null; method: LotAddendumIncentiveApplicationMethod }
  >({
    params: () => ({
      addendumId: this.addendumId(),
      amount: this.amount(),
      method: this.method()
    }),
    stream: ({ params }) => {
      if (!params.addendumId || !params.amount || params.amount <= 0) {
        return of(null);
      }
      return this.lotIncentiveService.previewIncentiveApplication(params.addendumId, {
        amount: params.amount,
        method: params.method
      });
    }
  });

  readonly isLoading = computed(() => this.previewResource.isLoading());
  // `error()` returns `Error | undefined`, so use `!= null` to catch both null and undefined.
  readonly hasError = computed(() => this.previewResource.error() != null && this.hasValidAmount());
  readonly preview = computed(() => this.previewResource.value() ?? null);
  readonly isEmpty = computed(() => {
    const data = this.preview();
    return !!data && (data.products?.length ?? 0) === 0;
  });

  readonly rows = computed<AllocationRow[]>(() => {
    const data = this.preview();
    if (!data) return [];
    return data.products.map((p: DsmAddendumIncentivePreviewProduct) => {
      const allocated = p.incentiveAmount ?? 0;
      const cost = p.amount ?? 0;
      const skipped = allocated <= 0;
      const percentage = cost > 0 ? Math.round((allocated / cost) * 10000) / 100 : 0;
      return {
        id: p.id,
        label: `${p.productCode} — Line ${p.lineNumber}`,
        cost,
        allocated,
        percentage,
        skipped
      };
    });
  });

  /** Sum of per-product allocations — used for the integrity tick. */
  readonly allocationsSum = computed(() => this.rows().reduce((sum, row) => sum + row.allocated, 0));

  readonly allocationsMatch = computed(() => {
    const data = this.preview();
    if (!data) return false;
    return Math.abs(this.allocationsSum() - data.allocatedAmount) < 0.01;
  });

  retry(): void {
    this.previewResource.reload();
  }
}
